import React, { useEffect, useState } from 'react';
import { Star, CheckCircle, Apple, Salad, Utensils, Zap, Flame, Heart, Target, Sparkles, ClipboardList, TrendingUp } from 'lucide-react';
import { fetchInstructorsByRole } from '../YogaInstructor/instructorPageApi';
import { Link, useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { getPillarMeta, getSpecialityLabel } from '../../../utils/breadcrumb';
import { SEO } from '../../SEO';

interface ApiInstructor {
  firstname: string;
  lastname: string;
  useremail: string;
  mentorId: string;
  profileIcon?: string;
  profileQuote?: string;
  bio?: string;
  languages?: string[];
  location?: string;
  phone?: string;
  createAt: string;
  updateAt: string;
  emailVerified: boolean;
  certification?: string[];
  experience?: string;
  socialLinks: {
    demoVideo?: string;
    facebook?: string;
    instagram?: string;
    telegram?: string;
    whatsapp?: string;
    youtube?: string;
  };
  Skills: string[];
  slug: string;
  latestClass?: {
    _id: string;
    classTitle: string;
    pricingType?: string;
  }[];
}

interface Instructor {
  mentorId: string;
  name: string;
  specialty: string;
  experience: string;
  rating: number;
  reviews: number;
  price: string;
  languages: string[];
  image: string;
  profileQuote?: string;
  certification: string[];
  description: string;
  availability: string;
  createdAt: string;
  demoVideoUrl?: string;
  socialLinks?: {
    demoVideo?: string;
    facebook?: string;
    instagram?: string;
    telegram?: string;
    whatsapp?: string;
    youtube?: string;
  };
  Skills: string[];
  slug: string;
  latestClassTitle?: string;
}

const transformApiToInstructor = (apiData: ApiInstructor): Instructor => {
  let quote = apiData.profileQuote;
  if (quote) {
    quote = quote.replace(/ style="[^"]*"/g, '').replace(/<p>/g, '').replace(/<\/p>/g, '');
  }

  let skills = apiData.Skills || [];
  if (skills.length === 1 && typeof skills[0] === 'string' && skills[0].includes(',')) {
    skills = skills[0].split(/,\s*/);
  }

  let priceValue = "Paid";
  let latestClassTitle: string | undefined;

  if (apiData.latestClass && apiData.latestClass.length > 0) {
    const firstClass = apiData.latestClass[0];
    latestClassTitle = firstClass.classTitle;
    if (firstClass.pricingType && (firstClass.pricingType.toLowerCase() === 'free' || firstClass.pricingType.toLowerCase() === 'demo')) {
      priceValue = "Free Demo";
    }
  }

  return {
    mentorId: apiData.mentorId,
    name: `${apiData.firstname} ${apiData.lastname}`,
    specialty: apiData.bio || 'Nutritional Expert',
    experience: apiData.experience || 'Experienced',
    rating: 4.8 + Math.random() * 0.2,
    reviews: Math.floor(Math.random() * 40) + 10,
    price: priceValue,
    languages: apiData.languages || ['English', 'Hindi'],
    image: apiData.profileIcon || '',
    profileQuote: quote,
    certification: apiData.certification || ['Certified Nutritionist'],
    description: apiData.bio || `Helping you achieve your fitness goals through personalized nutrition plans.`,
    availability: 'Available',
    createdAt: apiData.createAt,
    demoVideoUrl: apiData.socialLinks?.demoVideo,
    socialLinks: apiData.socialLinks,
    Skills: skills,
    slug: apiData.slug || '',
    latestClassTitle: latestClassTitle,
  };
};

export default function NutritionalDietPlanPage() {
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAndSetInstructors = async () => {
      try {
        setLoading(true);
        const res: any = await fetchInstructorsByRole('Nutritional Diet Plan');
        if (res && res.data && Array.isArray(res.data)) {
          setInstructors(res.data.map(transformApiToInstructor));
        } else {
          setError('No nutritionists found for this category.');
        }
      } catch (err) {
        setError('Failed to fetch nutritionists');
      } finally {
        setLoading(false);
      }
    };

    fetchAndSetInstructors();
    AOS.init({ duration: 800, once: true });
  }, []);

  const features = [
    { title: 'Custom Diet Plans', icon: <Salad className="text-emerald-500" />, desc: 'Weekly plans tailored to your food preferences and health goals.' },
    { title: 'Calorie Tracking', icon: <Flame className="text-orange-500" />, desc: 'Expert guidance on managing your daily intake for optimal results.' },
    { title: 'Grocery Lists', icon: <ClipboardList className="text-blue-500" />, desc: 'Simplified shopping lists to make your healthy journey easier.' },
    { title: 'Goal Monitoring', icon: <TrendingUp className="text-teal-500" />, desc: 'Regular follow-ups to track progress and adjust your plan.' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Personalized Nutritional Diet Plans & Clinical Nutritionists | MetaFit Wellness"
        description="Get customized nutrition and diet plans designed by certified clinical nutritionists at MetaFit Wellness. Weight management, gut health, and lifestyle meal plans."
        canonical="/nutritional-diet-plan"
        keywords="diet plan, nutritionist, clinical nutrition, weight loss diet, personalized meal plans, MetaFit Wellness"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-teal-50 to-white py-16 md:py-24">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 opacity-10">
          <Apple size={400} className="text-emerald-300 rotate-12" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-1.5 text-sm font-bold text-emerald-700 shadow-sm">
              <Sparkles size={16} />
              Eat Smart, Live Better
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 leading-tight">
              Personalized Nutrition <br />
              <span className="text-emerald-600">For Your Unique Body</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Ditch the generic diets. Connect with certified nutritionists who create 
              meal plans that actually work for your lifestyle, taste, and fitness goals.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#specialists" className="w-full sm:w-auto px-8 py-4 bg-emerald-600 text-white rounded-2xl font-bold text-lg hover:bg-emerald-700 transition shadow-lg shadow-emerald-200">
                Explore Nutritionists
              </a>
              <Link to="/ai-diet-nutrition" className="w-full sm:w-auto px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold text-lg hover:bg-slate-800 transition">
                Try AI Diet Assistant
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 -mt-10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 ring-1 ring-gray-100">
                  {feature.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialists Grid */}
      <section id="specialists" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-black text-slate-900 mb-4">Meet Our Nutrition Experts</h2>
              <p className="text-slate-500">
                All our nutritionists are certified professionals with expertise in weight management, 
                therapeutic diets, and sports nutrition.
              </p>
            </div>
            <div className="px-6 py-2 bg-emerald-50 text-emerald-700 rounded-2xl font-bold border border-emerald-100 shadow-sm">
              {instructors.length} Certified Experts
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-3 border-emerald-600 mx-auto"></div>
              <p className="mt-4 text-slate-500 font-medium">Curating your experts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-200">
              <Utensils className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-bold text-xl">{error}</p>
              <p className="text-slate-400 mt-2">Check back soon for new specialists!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {instructors.map((instructor) => (
                <div
                  key={instructor.mentorId}
                  data-aos="fade-up"
                  className="group bg-white rounded-[40px] border border-gray-100 p-6 hover:shadow-2xl hover:shadow-emerald-100 transition-all duration-500"
                >
                  <div className="relative mb-6">
                    <div className="w-full aspect-square rounded-[32px] overflow-hidden">
                      <img 
                        src={instructor.image || '/default-avatar.png'} 
                        alt={instructor.name} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-2xl flex items-center gap-1 shadow-lg">
                      <Star className="w-4 h-4 fill-emerald-500 text-emerald-500" />
                      <span className="font-bold text-slate-900">{instructor.rating.toFixed(1)}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-2xl font-black text-slate-900 mb-1">{instructor.name}</h3>
                    <p className="text-emerald-600 font-bold text-sm tracking-wide uppercase">{instructor.experience}</p>
                  </div>

                  <p className="text-slate-500 text-sm line-clamp-2 mb-6 min-h-[40px]">
                    {instructor.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {instructor.Skills.slice(0, 3).map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-xl text-xs font-bold">
                        {skill}
                      </span>
                    ))}
                  </div>

                  <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400 block uppercase font-bold tracking-widest">Pricing</span>
                      <span className="text-xl font-black text-slate-900">{instructor.price}</span>
                    </div>
                    <Link
                      to={`/public/instructor-profile/${instructor.slug}`}
                      state={{
                        sourcePillar: getPillarMeta(instructor.specialty || instructor.Skills?.[0] || 'Nourish'),
                        sourceSpeciality: getSpecialityLabel(instructor.specialty || instructor.Skills?.[0] || 'Nourish'),
                        sourceName: instructor.name,
                      }}
                      className="px-6 py-3 bg-emerald-600 text-white rounded-2xl font-bold text-sm hover:bg-emerald-700 transition shadow-md shadow-emerald-100"
                    >
                      View Plans
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 opacity-20 -translate-x-1/4 translate-y-1/4">
          <Utensils size={300} className="text-emerald-500" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
            Ready to Transform Your Health?
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Book a consultation today and get a diet plan that is sustainable, 
            nutritious, and enjoyable.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button className="px-10 py-4 bg-emerald-600 text-white rounded-2xl font-black text-lg hover:bg-emerald-700 transition">
              Get Started Now
            </button>
            <button className="px-10 py-4 bg-white text-slate-900 rounded-2xl font-black text-lg hover:bg-gray-100 transition">
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
